import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { authAxios } from '../../app/authAxios';

/**
 * Real-backend Reports surface (v1.14.0) — GET /admin/reports (permission:
 * view-reports) and GET /investor/reports (any authenticated investor,
 * `type` restricted to investments/transactions/support_tickets, no `kyc`).
 * Both stream a file directly (Content-Disposition: attachment) — this is
 * a binary download, not JSON, so it can't go through authBaseQuery (which
 * assumes every response is JSON). Built as an RTK Query mutation with a
 * custom queryFn instead, purely to get the same isLoading/error handling
 * every other action in this app already has — nothing here is cached or
 * invalidated, and there's no `GET /admin/reports/history` to back a
 * "recently generated" list with (the docs are explicit nothing is
 * persisted server-side), so that list lives as session-local component
 * state in the Reports pages, not in this API layer.
 */

// "attachment; filename=\"investments_2026-08-01_2026-08-22.csv\"" — quotes
// are optional per the header spec, so the regex tolerates either.
function extractFilename(contentDisposition, fallback) {
  if (!contentDisposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1]) : fallback;
}

function triggerBrowserDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// With `responseType: 'blob'`, axios hands back even a JSON error body
// (422/403/etc.) as a Blob — it never gets auto-parsed the way a normal
// JSON response would. Read it back out as text and JSON.parse it so
// authErrorMessage still has a real `err.data.message` to work with,
// instead of every failure looking like a generic network error.
async function parseBlobErrorResponse(axiosError) {
  const status = axiosError.response?.status;
  const blob = axiosError.response?.data;

  if (blob instanceof Blob) {
    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      return { status, data: parsed };
    } catch {
      // Body wasn't JSON (or was empty) — fall through to a generic shape.
    }
  }

  return { status, data: { message: axiosError.message } };
}

function buildReportParams({ type, format, date_from, date_to, status, category }) {
  const params = { type, format };
  if (date_from) params.date_from = date_from;
  if (date_to) params.date_to = date_to;
  if (status) params.status = status;
  if (category) params.category = category;
  return params;
}

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: fakeBaseQuery(), // every endpoint below supplies its own queryFn — no requests go through this
  tagTypes: [],
  endpoints: (builder) => ({
    // GET /admin/reports
    generateAdminReport: builder.mutation({
      async queryFn(args) {
        try {
          const response = await authAxios({
            url: '/admin/reports',
            method: 'GET',
            params: buildReportParams(args),
            responseType: 'blob',
          });
          const filename = extractFilename(
            response.headers['content-disposition'],
            `${args.type}.${args.format === 'xlsx' ? 'xlsx' : 'csv'}`
          );
          triggerBrowserDownload(response.data, filename);
          return { data: { filename } };
        } catch (axiosError) {
          return { error: await parseBlobErrorResponse(axiosError) };
        }
      },
    }),

    // GET /investor/reports — same shape, restricted type list enforced by
    // the page's dropdown (kyc isn't offered, not just hidden behind a
    // try/catch), but the 422 is still handled the same way as a backstop.
    generateInvestorReport: builder.mutation({
      async queryFn(args) {
        try {
          const response = await authAxios({
            url: '/investor/reports',
            method: 'GET',
            params: buildReportParams(args),
            responseType: 'blob',
          });
          const filename = extractFilename(
            response.headers['content-disposition'],
            `${args.type}.${args.format === 'xlsx' ? 'xlsx' : 'csv'}`
          );
          triggerBrowserDownload(response.data, filename);
          return { data: { filename } };
        } catch (axiosError) {
          return { error: await parseBlobErrorResponse(axiosError) };
        }
      },
    }),
  }),
});

export const { useGenerateAdminReportMutation, useGenerateInvestorReportMutation } = reportsApi;
